import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";

export default function PublicForm() {
  const { slug } = useParams();
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitter, setSubmitter] = useState({ name: "", email: "" });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/public/forms/${slug}`);
        if (!r.ok) throw new Error(await r.text());
        const d = await r.json();
        setForm(d);
      } catch (e) { setError("Form not found or no longer active."); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  const submit = async () => {
    // required field check
    const missing = (form.fields || []).filter((f) => f.required && !answers[f.id || f.label]);
    if (missing.length > 0) return toast.error(`Please complete: ${missing.map((m) => m.label).join(", ")}`);
    setSubmitting(true);
    try {
      const r = await fetch(`${API}/public/forms/${slug}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ responses: answers, submitted_by_name: submitter.name, submitted_by_email: submitter.email }),
      });
      if (!r.ok) throw new Error(await r.text());
      setSubmitted(true);
    } catch (e) { toast.error("Could not submit. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (loading) return <FormShell><div className="text-center py-8 text-[#57534E]">Loading form…</div></FormShell>;
  if (error) return <FormShell><div className="text-center py-8"><p className="text-sm text-[#57534E]">{error}</p></div></FormShell>;
  if (submitted) return (
    <FormShell orgName={form.org_name}>
      <div className="text-center py-8">
        <CheckCircle2 size={40} className="mx-auto text-green-600 mb-3" strokeWidth={1.5} />
        <p className="text-lg font-display font-semibold">{form.success_message || "Thank you!"}</p>
        <p className="text-sm text-[#57534E] mt-1">Your response has been received.</p>
      </div>
    </FormShell>
  );

  return (
    <FormShell orgName={form.org_name}>
      <div className="space-y-5" data-testid="public-form">
        <div>
          <h1 className="font-display font-bold text-2xl tracking-tight">{form.name}</h1>
          {form.description && <p className="text-sm text-[#57534E] mt-1 whitespace-pre-wrap">{form.description}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div><Label>Your name</Label>
            <Input value={submitter.name} onChange={(e) => setSubmitter({...submitter, name: e.target.value})} className="mt-1.5" data-testid="submitter-name" />
          </div>
          <div><Label>Your email</Label>
            <Input type="email" value={submitter.email} onChange={(e) => setSubmitter({...submitter, email: e.target.value})} className="mt-1.5" data-testid="submitter-email" />
          </div>
        </div>
        <div className="border-t border-[#E8E2D9] pt-4 space-y-4">
          {(form.fields || []).map((f, i) => (
            <FieldInput key={f.id || i} field={f} value={answers[f.id || f.label] || ""} onChange={(v) => setAnswers({ ...answers, [f.id || f.label]: v })} />
          ))}
        </div>
        <Button data-testid="public-form-submit" onClick={submit} disabled={submitting} className="rounded-full bg-[#E8712C] hover:bg-[#C55A1F] w-full">
          {submitting ? "Submitting…" : (form.submit_button_label || "Submit")}
        </Button>
      </div>
    </FormShell>
  );
}

function FormShell({ children, orgName }) {
  return (
    <div className="min-h-screen bg-[#FAF8F5] py-10 px-4">
      <div className="max-w-2xl mx-auto">
        {orgName && <div className="text-center text-sm text-[#57534E] mb-6 uppercase tracking-widest">{orgName}</div>}
        <div className="bg-white rounded-3xl border border-[#E8E2D9] p-8">{children}</div>
        <div className="text-center text-xs text-[#A8A29E] mt-6">Powered by ClientDesk VA</div>
      </div>
    </div>
  );
}

function FieldInput({ field, value, onChange }) {
  const label = <Label className="text-sm">{field.label}{field.required && <span className="text-[#E8712C]"> *</span>}</Label>;
  const common = { value, onChange: (e) => onChange(e.target.value), placeholder: field.placeholder || "" };

  if (field.type === "textarea") return <div>{label}<Textarea rows={3} {...common} className="mt-1.5" data-testid={`pf-${field.id}`} /></div>;
  if (field.type === "select") return (
    <div>{label}
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="mt-1.5" data-testid={`pf-${field.id}`}><SelectValue placeholder="Select…" /></SelectTrigger>
        <SelectContent>{(field.options || []).filter((o) => o && String(o).trim()).map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  if (field.type === "checkbox") return (
    <label className="flex items-center gap-2 text-sm cursor-pointer">
      <input type="checkbox" checked={value === true || value === "true"} onChange={(e) => onChange(e.target.checked)} className="w-4 h-4 accent-[#E8712C]" data-testid={`pf-${field.id}`} />
      {field.label}
    </label>
  );
  if (field.type === "radio") return (
    <div>{label}
      <div className="mt-1.5 space-y-1.5">
        {(field.options || []).map((o) => (
          <label key={o} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="radio" name={field.id} value={o} checked={value === o} onChange={() => onChange(o)} className="accent-[#E8712C]" />
            {o}
          </label>
        ))}
      </div>
    </div>
  );
  const inputType = { number: "number", date: "date", email: "email", phone: "tel" }[field.type] || "text";
  return <div>{label}<Input type={inputType} {...common} className="mt-1.5" data-testid={`pf-${field.id}`} /></div>;
}
